import EtPersonalJson from "@/ts/components/EtPersonalJson";
import FrontendHandler from "@/ts/components/Handlers/FrontendHandler";
import ApiHandler from "@/ts/components/Handlers/ApiHandler";
import {Offcanvas} from "bootstrap";

export class Warenkorb {
    private cartPreviewItems: HTMLElement | null = null;
    private readonly bsOffcanvas: Offcanvas | null = null;
    private readonly offcanvasElement: HTMLElement | null = null;
    private cartPreviewElement: HTMLElement | null = null;
    private fieldDebounceTimers: Map<HTMLInputElement, number> = new Map();
    private scrollStep: number = 300;
    // @ts-ignore
    private resizeObserver: ResizeObserver | null = null;

    constructor() {
        this.offcanvasElement = document.querySelector("#offcanvasCart") as HTMLElement | null;
        if (!this.offcanvasElement) throw new Error("No offcanvasElement found");

        this.bsOffcanvas = new Offcanvas(this.offcanvasElement);
        this.observeBuyButtons();

        document.getElementById("layout_header_button_cart")?.addEventListener("click", () => this.openCartPreview());

        // Aktualisiere den Warenkorb anhand von PJ, da dieser immer bei Wk-Aktualisierungen geholt wird
        EtPersonalJson.getInstance().addEventHandler((data) => {
            const elements = document.querySelectorAll<HTMLElement>('[data-et-pj-dom-html="cart-dropdown"]');
            elements.forEach(el => {
                const fallback = el.dataset.emptyCartTemplate;
                const fallbackContent = fallback ? document.querySelector(`.${fallback}`) : null;
                el.innerHTML = data?.dom?.['cart-dropdown']?.html || fallbackContent?.innerHTML || '';
            });

            // Event-Listener & Slider neu initialisieren
            this.refreshCartPreview();

            // PJ-Counter updaten
            const counters = document.querySelectorAll<HTMLElement>('[data-et-pj-counter-name="cart"]');
            counters.forEach(counter => {
                const numItems = data?.cart?.numItems ?? 0;
                counter.dataset.etPjCounterValue = numItems.toString();
            });
        });

        this.refreshCartPreview();
    }

    /**
     * Erfasst alle Kaufen-Buttons, auch wenn sie später erscheinen (z.B. im Modal)
     * @private
     */
    private observeBuyButtons() {
        document.querySelectorAll<HTMLButtonElement>(".buy-button").forEach(btn => this.attachBuyHandler(btn));
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.type === "childList") {
                    mutation.addedNodes.forEach(node => {
                        if (!(node instanceof HTMLElement)) return;
                        if (node.matches(".buy-button")) this.attachBuyHandler(node as HTMLButtonElement);
                        node.querySelectorAll<HTMLButtonElement>(".buy-button").forEach(btn => this.attachBuyHandler(btn));
                    });
                }
            }
        });
        observer.observe(document.body, {childList: true, subtree: true});
    }

    /**
     * Aktualisiere die View und setze Event-Listener.
     * Elemente werden geklont, um mehrfache Verwendung (z.B. mit race-conditions) auszuschließen.
     * @private
     */
    private refreshCartPreview() {
        this.cartPreviewElement = this.offcanvasElement?.querySelector('.personal_warenkorb_preview') ?? null;
        if (!this.cartPreviewElement) return;

        this.cartPreviewItems = this.cartPreviewElement.querySelector('.personal_warenkorb_preview__items');

        this.cartPreviewElement.querySelectorAll(".warenkorb_element_preview_entry_remove_button").forEach(b => b.replaceWith(b.cloneNode(true)));
        this.cartPreviewElement.querySelectorAll(".et_text_without_arrows").forEach(f => f.replaceWith(f.cloneNode(true)));
        this.cartPreviewElement.querySelectorAll(".plusminusbutton").forEach(b => b.replaceWith(b.cloneNode(true)));

        this.cartPreviewElement.querySelectorAll(".warenkorb_element_preview_entry_remove_button")
            .forEach(b => this.attachRemoveButton(b as HTMLButtonElement));
        this.cartPreviewElement.querySelectorAll(".et_text_without_arrows")
            .forEach(f => this.attachField(f as HTMLInputElement));
        this.cartPreviewElement.querySelectorAll(".plusminusbutton")
            .forEach(b => this.attachPlusMinusButton(b as HTMLButtonElement));

        this.observeCartHeight();
    }

    /**
     * Setzt alle Actions für Buy-Buttons auf der Seite
     * @param button
     * @private
     */
    private attachBuyHandler(button: HTMLButtonElement) {
        button.addEventListener("click", async (event) => {
            event.preventDefault();
            const addBtn = (event.target as HTMLElement)
                .closest<HTMLButtonElement>(".element_artikel_warenkorb__a2c, .buy_selected");
            if (!addBtn) return;

            const containerEl = addBtn.closest(".element_artikel_warenkorb") ?? document;
            const artikelId = Number(addBtn.dataset.artikelId ?? 0);
            const buyUrl = addBtn.dataset.buyUrl ?? "";
            let menge = 1;

            if (addBtn.dataset.getMengeFrom) {
                const mengeEl = document.getElementById(addBtn.dataset.getMengeFrom)
                    ?? containerEl.querySelector<HTMLInputElement>("." + addBtn.dataset.getMengeFrom);
                if (mengeEl instanceof HTMLInputElement) menge = Number(mengeEl.value) || 1;
            }

            if (artikelId > 0 && menge > 0 && buyUrl) {
                try {
                    await FrontendHandler.blockSubmitAndShowLoading(async () => {
                        await this.addArtikel(buyUrl, artikelId.toString(), "", menge.toString());
                        this.openCartPreview(true);
                    }, document.body, addBtn);
                } catch (error) {
                    console.error("[Warenkorb] Error adding article:", error);
                }
            }
        });
    }

    /**
     * Artikel-Entfernen-Button
     * @param button
     * @private
     */
    private async attachRemoveButton(button: HTMLButtonElement) {
        button.addEventListener("click", async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const url = button.dataset.deleteUrl;
            if (!url) return;
            await FrontendHandler.blockSubmitAndShowLoading(async () => {
                await this.removeArtikel(url, button);
            }, document.body, button);
        });
    }

    /**
     * Artikel-Anzahl-Ändern Plus und Minus Buttons
     * @param button
     * @private
     */
    private attachPlusMinusButton(button: HTMLButtonElement) {
        button.addEventListener("click", async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const inputField = button.parentElement?.querySelector<HTMLInputElement>("input");
            if (!inputField) return;
            await this.getFieldEvent(e, inputField);
        })
    }

    /**
     * Artikel-Anzahl-Ändern Plus und Minus Input-Feld
     * @param field
     * @private
     */
    private async attachField(field: HTMLInputElement) {
        field.addEventListener("change", async (e) => {
            this.getFieldEvent(e, field);
        });
    }

    /**
     * Artikel-Anzahl-Ändern Event
     * @param event
     * @param field
     */
    private getFieldEvent = (event: Event, field: HTMLInputElement) => {
        event.preventDefault();
        event.stopPropagation();

        const url = field.dataset.changeUrl;
        const posId = field.dataset.wkArtikelId;
        const menge = Number(field.value);
        if (!url || !posId || isNaN(menge)) return;

        const existingTimer = this.fieldDebounceTimers.get(field);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }

        const timer = window.setTimeout(async () => {
            await this.updateWarenkorbPosition(url, menge.toString(), posId);
            this.fieldDebounceTimers.delete(field);
        }, 300);

        this.fieldDebounceTimers.set(field, timer);
    }

    /**
     * Artikel hinzufügen (auf der ganzen Seite nutzbar)
     * @param url
     * @param artikelId
     * @param lpvbs
     * @param menge
     */
    public async addArtikel(url: string, artikelId: string, lpvbs = "", menge = "1") {
        menge = menge.replace(",", ".");
        try {
            await ApiHandler.getJsonFetch({
                service: "getViewContent",
                url: encodeURI(url.replace(/&amp;/g, "&")),
                praesenz: window.etData.presenceId.toString()
            }, {menge0: menge, ac_id0: artikelId, add0: artikelId, lpvbs});
            await EtPersonalJson.getInstance().fetchData(false);
            if (typeof window.trackPeeriusCart === "function") window.trackPeeriusCart(artikelId, menge);
        } catch (error) {
            console.error("[Warenkorb] Error in addArtikel:", error);
        }
    }

    /**
     * Artikel entfernen (auf der ganzen Seite nutzbar)
     * @param url
     * @param button
     */
    public async removeArtikel(url: string, button: HTMLButtonElement) {
        try {
            await FrontendHandler.blockSubmitAndShowLoading(async () => {
                await ApiHandler.getJsonFetch({
                    service: "getViewContent",
                    url: encodeURI(url.replace(/&amp;/g, "&")),
                    praesenz: window.etData.presenceId.toString()
                });
                await EtPersonalJson.getInstance().fetchData(false);
            }, document.body, button);
        } catch (error) {
            console.error("[Warenkorb] Error in removeArtikel:", error);
        }
    }

    /**
     * Artikel-Anzahl ändern
     * @param fetchUrl
     * @param menge
     * @param bPositionId
     */
    public async updateWarenkorbPosition(fetchUrl: string, menge: string, bPositionId: string) {
        if (!fetchUrl || !bPositionId) return;
        const payload = new URLSearchParams();
        payload.append(`wk_menge[${bPositionId}]`, menge.replace(",", "."));
        try {
            showSpinner();
            const response = await fetch(fetchUrl, {
                method: "POST",
                headers: {"Content-Type": "application/x-www-form-urlencoded"},
                body: payload.toString(),
                credentials: "same-origin"
            });
            if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);
            await EtPersonalJson.getInstance().fetchData(false);
            hideSpinner();
        } catch (error) {
            hideSpinner();
            console.error("[Warenkorb] Error updating position:", error);
        }
    }

    /**
     * Definiere maximale Höhe für den Scroll-Container
     * @param container
     * @private
     */
    private setContainerHeight(container: HTMLElement) {
        const offcanvas = this.offcanvasElement;
        if (!offcanvas) return;

        const header = offcanvas.querySelector<HTMLElement>('.offcanvas-header')?.offsetHeight || 0;
        const footer = offcanvas.querySelector<HTMLElement>('.offcanvas-footer')?.offsetHeight || 0;
        const upBtn = offcanvas.querySelector<HTMLElement>('.personal_warenkorb_preview__items_up')?.offsetHeight || 0;
        const downBtn = offcanvas.querySelector<HTMLElement>('.personal_warenkorb_preview__items_down')?.offsetHeight || 0;
        const sum = offcanvas.querySelector<HTMLElement>('.personal_warenkorb_preview__sum')?.offsetHeight || 0;

        const maxHeight = window.innerHeight - header - footer - upBtn - downBtn - sum - 10;

        container.style.maxHeight = `${maxHeight}px`;
        container.style.overflowY = "auto";

        this.initCartSlider();
    }

    /**
     * Stelle Scrolling-Funktionalität her
     * @private
     */
    private initCartSlider() {
        const container = document.querySelector<HTMLElement>('.personal_warenkorb_preview__items_scroll');
        const upBtn = document.querySelector<HTMLButtonElement>('.personal_warenkorb_preview__items_up');
        const downBtn = document.querySelector<HTMLButtonElement>('.personal_warenkorb_preview__items_down');
        if (!container || !upBtn || !downBtn) return;
        const scrollUp = () => {
            container.scrollBy({top: -this.scrollStep, behavior: 'smooth'});
        };
        const scrollDown = () => {
            container.scrollBy({top: this.scrollStep, behavior: 'smooth'});
        };
        upBtn.addEventListener('click', scrollUp);
        downBtn.addEventListener('click', scrollDown);
    }

    private observeCartHeight() {
        if (!this.cartPreviewItems) return;

        if (!this.resizeObserver) {
            // @ts-ignore
            this.resizeObserver = new ResizeObserver(() => {
                this.setContainerHeight(this.cartPreviewItems!);
            });
        }

        this.resizeObserver.observe(this.cartPreviewItems);
    }

    /**
     * Öffne Warenkorb-Sidebar
     */
    public openCartPreview(added: boolean = false) {
        if (added) {
            document.getElementById('offcanvasCartLabel').classList.remove('d-none');
        } else {
            document.getElementById('offcanvasCartLabel').classList.add('d-none');
        }
        if (!this.bsOffcanvas || !this.offcanvasElement) return;
        this.bsOffcanvas.show();
        this.refreshCartPreview();
    }
}
